/**
 * Aperçu du financement pour l'écran 2 (PRD §11 « affiché vs après réaction », ADR 0004 §4).
 * « Affiché » = arithmétique d'un tract : solde de base + recettes brutes − coût des mesures, sans réaction de
 * l'économie. « Après réaction » = première année simulée par le moteur (fuite, multiplicateurs, taux, confiance),
 * sans événement. Pur : aucune constante, tout vient de la baseline et des params.
 */
import { adoptionPoliticalCost, needGap, previewFinancing } from "./financing.js";
import { param } from "./params.js";
import { createGame } from "./simulate.js";
import { initState, PCT } from "./state.js";
import { simulateYear } from "./year.js";
import type { Baseline, Financing, Measure, ParamSet, Programme, YearState } from "./types.js";

export interface ProgrammePreview {
  /** Besoin net de financement des mesures en régime (Md€/an, > 0 = à financer). */
  gap_bn: number;
  /** Recettes et économies telles qu'affichées (brutes). */
  raised_static_bn: number;
  /** Après fuite / évitement / perte d'efficacité du rabot (première année). */
  raised_after_reaction_bn: number;
  /** Part du besoin laissée à la dette (après réaction, ≥ 0). */
  debt_residual_bn: number;
  /** Déficit APU « tract » : solde de base + recettes brutes − coût des mesures, en % du PIB nominal tendanciel de l'année 1 (négatif = déficit). */
  deficit_static_pct: number;
  /** Déficit APU de la première année simulée (% du PIB, négatif = déficit). */
  deficit_simulated_pct: number;
  /**
   * Dette/PIB « tract » : (dette de base − solde « tract ») / PIB nominal tendanciel de l'année 1 (croissance potentielle et
   * inflation de base, sans aucune réaction de l'économie). ADR 0005 : le PIB figé de la v0.1 rendait la dette « affichée »
   * plus lourde que la dette simulée, à rebours du message « la réalité est plus dure que le tract ».
   */
  debt_static_pct: number;
  /** Dette/PIB de la première année simulée. */
  debt_simulated_pct: number;
  /** Capital politique consommé chaque année par le financement (leviers + rabot). */
  political_cost_per_year: number;
  /** Capital politique consommé CHAQUE ANNÉE par les mesures du programme en régime (règle symétrique, ADR 0009 ; v0.2 : une fois à l'adoption). */
  political_cost_programme: number;
  /** Économie brute du rabot par point (Md€), pour le libellé du curseur. */
  rabot_bn_per_pt: number;
  first_year: YearState;
}

export function previewProgramme(
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  catalogue: Measure[],
  seed = 0,
): ProgrammePreview {
  const content = { measures: catalogue, events: [] };
  const g = createGame(baseline, params, programme, financing, content);
  const v = g.view;
  const st = v.start;
  // Dépense tendancielle des missions la première année (même base que le moteur : inflation + croissance réelle tendancielle).
  const f1 = 1 + (st.inflation + param(params, "mission_trend_real_growth_pct")) / PCT;
  const missionsSpend1 = v.missions_total * f1;
  const credits1 = Object.fromEntries(v.missions.map((m) => [m.id, m.credits * f1]));
  const fin = previewFinancing(params, financing, missionsSpend1, credits1, v.revenue_yields);
  const gap = needGap(programme, catalogue);
  // « Tract » = tendanciel de l'année 1 (simulé sans programme, financement ni événement : même calage que le jeu) + recettes
  // brutes − coût des mesures, rapportés au PIB tendanciel de l'année 1 (aucune réaction de l'économie au programme). ADR 0014 §3 :
  // l'ancienne formule (solde de départ + calage) supposait un tendanciel égal au solde de départ.
  const sTrend = initState(baseline, params, { measure_ids: [], hyp: {} }, { measures: [], byId: {} });
  const trend1 = simulateYear(sTrend, { view: v, params, financing: { revenue_raises: {}, across_the_board_cut: 0 }, measures: [], eventsById: {} }).year;
  const gdp1 = trend1.kpis["gdp_bn"] ?? st.gdp;
  const staticBalance = trend1.account.balance_apu + fin.static_total - gap;
  const deficitStatic = (staticBalance / gdp1) * PCT;
  // Première année simulée sans vote (la censure est affichée à part par l'UI) ni événement.
  const s0 = initState(baseline, params, programme, { measures: g.measures, byId: Object.fromEntries(catalogue.map((m) => [m.id, m])) });
  const first = simulateYear(s0, { view: v, params, financing, measures: g.measures, eventsById: {} }).year;
  void seed; // réservé : l'aperçu est déterministe et n'utilise pas d'aléa
  return {
    gap_bn: gap,
    raised_static_bn: fin.static_total,
    raised_after_reaction_bn: fin.net_total,
    debt_residual_bn: Math.max(0, gap - fin.net_total),
    deficit_static_pct: deficitStatic,
    deficit_simulated_pct: first.kpis["deficit_pct_gdp"] ?? NaN,
    debt_static_pct: ((st.debt - staticBalance) / gdp1) * PCT,
    debt_simulated_pct: first.kpis["debt_pct_gdp"] ?? NaN,
    political_cost_per_year: fin.political_cost,
    political_cost_programme: adoptionPoliticalCost(params, g.measures, programme.hyp),
    rabot_bn_per_pt: missionsSpend1 / PCT,
    first_year: first,
  };
}
